import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { useToast } from '../../hooks/use-toast';
import { adminApi } from '../../lib/adminApi';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { DatabaseBackup, Download, UploadCloud, CheckCircle2, XCircle, Server, Loader2, FileJson, History, RotateCcw, FileUp, Mail } from 'lucide-react';

const BackupManager = () => {
  const [status, setStatus] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [backups, setBackups] = useState([]);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [uploadPct, setUploadPct] = useState(null);
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);
  const { toast } = useToast();

  const load = async () => {
    try {
      const res = await adminApi.get('/backup/status');
      setStatus(res.data);
      return res.data;
    } catch (e) {
      toast({ title: 'Load failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
      return null;
    }
  };

  useEffect(() => {
    load();
    loadBackups();
    return () => clearInterval(pollRef.current);
  }, []);

  const loadBackups = async () => {
    try {
      const res = await adminApi.get('/backup/list');
      setBackups(res.data.backups || []);
    } catch (e) { /* FTP may not be configured */ }
  };

  const saveBlob = (blob, name) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadFull = async () => {
    setDownloading(true);
    setProgress(0);
    try {
      const prep = await adminApi.post('/backup/prepare', {}, { timeout: 300000 });
      const { token, size, filename } = prep.data;
      const parts = [];
      let offset = 0;
      while (offset < size) {
        const res = await adminApi.get(`/backup/chunk/${token}`, {
          params: { offset }, responseType: 'arraybuffer', timeout: 120000,
        });
        parts.push(res.data);
        offset += res.data.byteLength;
        setProgress(Math.min(100, Math.round((offset / size) * 100)));
        if (res.data.byteLength === 0) break;
      }
      saveBlob(new Blob(parts, { type: 'application/zip' }), filename);
      toast({ title: 'Backup downloaded', description: `Full backup (${(size / (1024 * 1024)).toFixed(1)} MB) saved.` });
    } catch (e) {
      toast({ title: 'Download failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setDownloading(false); setProgress(0); }
  };

  const downloadDataOnly = async () => {
    try {
      const res = await adminApi.get('/backup/download', { responseType: 'blob', timeout: 120000 });
      saveBlob(res.data, `ewrf_data_backup_${new Date().toISOString().slice(0, 10)}.zip`);
      toast({ title: 'Data backup downloaded', description: 'Database-only backup ZIP saved.' });
    } catch (e) {
      toast({ title: 'Download failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      await adminApi.post('/backup/sync-now', {}, { timeout: 60000 });
      toast({ title: 'Sync started', description: 'Uploading backup to cPanel in the background…' });
      pollRef.current = setInterval(async () => {
        const s = await load();
        if (s && !s.sync_running) {
          clearInterval(pollRef.current);
          setSyncing(false);
          loadBackups();
          if (s.last_sync_status === 'success') {
            toast({ title: 'Synced to cPanel', description: `Uploaded ${s.last_sync_file}` });
          } else if (s.last_sync_status === 'failed') {
            toast({ title: 'Sync failed', description: s.last_error, variant: 'destructive' });
          }
        }
      }, 4000);
    } catch (e) {
      setSyncing(false);
      toast({ title: 'Sync failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  const pollRestore = () => {
    setRestoring(true);
    pollRef.current = setInterval(async () => {
      const s = await load();
      if (s && !s.restore_running) {
        clearInterval(pollRef.current);
        setRestoring(false);
        if (s.last_restore_status === 'success') {
          toast({ title: 'Restore complete', description: s.last_restore_detail });
        } else if (s.last_restore_status === 'failed') {
          toast({ title: 'Restore failed', description: s.last_restore_error, variant: 'destructive' });
        }
      }
    }, 4000);
  };

  const confirmRestore = async () => {
    const target = restoreTarget;
    setRestoreTarget(null);
    try {
      await adminApi.post('/backup/restore', { source: 'ftp', filename: target.name, confirm_wipe: true });
      toast({ title: 'Restore started', description: `Rebuilding site from ${target.name}…` });
      pollRestore();
    } catch (e) {
      toast({ title: 'Restore failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  const restoreFromFile = async (file) => {
    if (!file || !file.name.endsWith('.zip')) {
      toast({ title: 'Invalid file', description: 'Please choose a backup .zip file', variant: 'destructive' });
      return;
    }
    setUploadPct(0);
    try {
      const init = await adminApi.post('/backup/restore-upload/init');
      const { token, chunk_size } = init.data;
      let offset = 0;
      while (offset < file.size) {
        const blob = file.slice(offset, offset + chunk_size);
        const buf = await blob.arrayBuffer();
        await adminApi.post(`/backup/restore-upload/chunk/${token}`, buf, {
          params: { filename: file.name },
          headers: { 'Content-Type': 'application/octet-stream' },
          timeout: 120000,
        });
        offset += buf.byteLength;
        setUploadPct(Math.min(100, Math.round((offset / file.size) * 100)));
      }
      await adminApi.post('/backup/restore', { source: 'upload', token, confirm_wipe: true });
      toast({ title: 'Restore started', description: `Rebuilding site from ${file.name}…` });
      pollRestore();
    } catch (e) {
      toast({ title: 'Restore failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setUploadPct(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const fmtSize = (b) => (b ? `${(b / (1024 * 1024)).toFixed(1)} MB` : '—');
  const fmtTime = (t) => (t ? new Date(t).toLocaleString() : 'Never');
  const busy = syncing || status?.sync_running;
  const restoreBusy = restoring || status?.restore_running;

  return (
    <div className="space-y-6" data-testid="backup-manager">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DatabaseBackup className="w-5 h-5 text-[#1565C0]" /> Site Backup
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-600">
            A full backup contains every database collection (all site content) as JSON plus every uploaded
            image, video and PDF — everything needed to restore the site.
          </p>
          <div className="flex flex-wrap gap-3">
            <Button onClick={downloadFull} disabled={downloading} className="bg-[#1565C0] hover:bg-[#0D47A1]" data-testid="backup-download-btn">
              {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              {downloading ? `Downloading… ${progress}%` : 'Download Full Backup (data + files)'}
            </Button>
            <Button onClick={downloadDataOnly} variant="outline" data-testid="backup-download-data-btn">
              <FileJson className="w-4 h-4 mr-2" /> Download Data Only (fast)
            </Button>
            <Button onClick={syncNow} disabled={busy || !status?.ftp_configured} variant="outline" data-testid="backup-sync-btn">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UploadCloud className="w-4 h-4 mr-2" />}
              {busy ? 'Uploading to cPanel…' : 'Sync to cPanel Now'}
            </Button>
          </div>
          {downloading && (
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div className="bg-[#1565C0] h-2 rounded-full transition-all" style={{ width: `${progress}%` }} />
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Server className="w-5 h-5 text-[#1565C0]" /> Automatic cPanel Sync (FTP)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          {status?.ftp_configured ? (
            <div className="flex items-center gap-2 text-emerald-700" data-testid="ftp-configured-badge">
              <CheckCircle2 className="w-4 h-4" /> Connected to <strong>{status.ftp_host}</strong> — backups upload daily to <code>/{status.ftp_directory}</code>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-amber-700" data-testid="ftp-not-configured-badge">
              <XCircle className="w-4 h-4" /> FTP not configured yet. Provide your cPanel FTP host, username and password to enable automatic daily sync.
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-2 text-gray-600">
            <div>Last sync: <strong data-testid="backup-last-sync">{fmtTime(status?.last_sync_at)}</strong></div>
            <div>Status: <strong className={status?.last_sync_status === 'success' ? 'text-emerald-600' : status?.last_sync_status === 'failed' ? 'text-red-600' : ''}>{status?.sync_running ? 'running…' : status?.last_sync_status || '—'}</strong></div>
            <div>File: <strong>{status?.last_sync_file || '—'}</strong></div>
            <div>Size: <strong>{fmtSize(status?.last_sync_size)}</strong></div>
          </div>
          {status?.last_error && <p className="text-red-600 text-xs">Last error: {status.last_error}</p>}
          <div className="flex items-center gap-2 pt-1 border-t text-gray-600">
            <Mail className="w-4 h-4" />
            Email alerts on failure:&nbsp;
            {status?.alerts_configured
              ? <strong className="text-emerald-600" data-testid="alerts-on-badge">enabled</strong>
              : <strong className="text-amber-600" data-testid="alerts-off-badge">not configured</strong>}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <History className="w-5 h-5 text-[#1565C0]" /> Restore from Backup
            </span>
            <Button size="sm" variant="ghost" onClick={loadBackups} data-testid="backup-list-refresh-btn">Refresh list</Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          <p className="text-gray-600">
            Rebuild the whole site from a previous backup. <strong className="text-red-600">Warning:</strong> this
            replaces ALL current site content with the backup's content.
          </p>
          {restoreBusy && (
            <div className="flex items-center gap-2 text-[#1565C0]" data-testid="restore-running-indicator">
              <Loader2 className="w-4 h-4 animate-spin" /> Restoring… {status?.restore_progress || ''}
            </div>
          )}
          {backups.length > 0 ? (
            <div className="divide-y border rounded-lg" data-testid="backup-list">
              {backups.map((b) => (
                <div key={b.name} className="flex items-center justify-between px-4 py-2.5">
                  <div>
                    <div className="font-medium text-gray-800">{b.name}</div>
                    <div className="text-xs text-gray-500">{fmtSize(b.size)} · {b.modified ? new Date(b.modified).toLocaleString() : ''}</div>
                  </div>
                  <Button size="sm" variant="outline" disabled={restoreBusy}
                    onClick={() => setRestoreTarget(b)} data-testid={`restore-btn-${b.name}`}>
                    <RotateCcw className="w-4 h-4 mr-1" /> Restore
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500">No backups found on cPanel yet.</p>
          )}
          <div className="flex items-center gap-3 pt-2 border-t">
            <input ref={fileInputRef} type="file" accept=".zip" className="hidden"
              onChange={(e) => restoreFromFile(e.target.files?.[0])} data-testid="restore-file-input" />
            <Button variant="outline" disabled={restoreBusy || uploadPct !== null}
              onClick={() => fileInputRef.current?.click()} data-testid="restore-upload-btn">
              {uploadPct !== null ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileUp className="w-4 h-4 mr-2" />}
              {uploadPct !== null ? `Uploading… ${uploadPct}%` : 'Restore from a ZIP on my computer'}
            </Button>
          </div>
          {status?.last_restore_at && (
            <div className="text-xs text-gray-500">
              Last restore: {fmtTime(status.last_restore_at)} — <strong className={status.last_restore_status === 'success' ? 'text-emerald-600' : 'text-red-600'}>{status.last_restore_status}</strong>
              {status.last_restore_detail ? ` (${status.last_restore_detail})` : ''}
              {status.last_restore_error ? ` — ${status.last_restore_error}` : ''}
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!restoreTarget} onOpenChange={(o) => !o && setRestoreTarget(null)}>
        <AlertDialogContent data-testid="restore-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Restore the whole site?</AlertDialogTitle>
            <AlertDialogDescription>
              This will REPLACE all current site content with the contents of <strong>{restoreTarget?.name}</strong>.
              Anything changed since that backup will be lost. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="restore-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRestore} className="bg-red-600 hover:bg-red-700" data-testid="restore-confirm-btn">
              Yes, restore
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default BackupManager;
